import {
  ActivityType,
  calculatedAreaActivityTypes,
} from '@shared/models/design/activities/activity-type.model';
import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivityReference } from '@shared/models/design/storey.model';
import { v4 } from 'uuid';
import { activityRefGuards } from '../guards/activity-guards.helpers';

/**
 * Create a reference to an activity (from a storey).
 * This is used to say that an activity is present on a storey and how much of the floor area is occupied by the activity.
 * @param type
 * @param share
 * @returns
 */
export const createActivityReference = (
  activity_id: Activity['id'],
  type: ActivityType,
  share: number = 1,
): ActivityReference => {
  return {
    type,
    id: v4(),
    activity_id,
    // Calculated refs don't have a share
    share: activityRefGuards.is.calculatedAreaActivityType(type)
      ? undefined
      : share,
  };
};

export const createActivityReferences = (
  activityRefs?: ActivityReference[],
): ActivityReference[] => {
  if (!activityRefs) return [];
  return activityRefs.map((ref) => ({
    ...ref,
    id: v4(),
  }));
};

/**
 * Calculated-area refs for a new storey, pointing at the design-level
 * activities. Project validation requires these on every storey.
 */
export const createCalculatedAreaActivityRefs = (
  activities: ReadonlyArray<Pick<Activity, 'id' | 'type'>>,
): ActivityReference[] =>
  calculatedAreaActivityTypes.flatMap((type) => {
    const activity = activities.find((candidate) => candidate.type === type);
    if (!activity) return [];
    return [createActivityReference(activity.id, type)];
  });

/**
 * Program refs for a storey that has none yet, pointing at preset
 * {@link Activity} rows on the design (for example Residential on a template).
 * Shares split the floor evenly and sum to 1.
 */
export const createProgramActivityRefs = (
  activities: ReadonlyArray<Pick<Activity, 'id' | 'type'>>,
): ActivityReference[] => {
  const programs = activities.filter((activity) =>
    activityRefGuards.is.programActivityType(activity.type),
  );
  if (programs.length === 0) return [];
  const share = 1 / programs.length;
  return programs.map((activity, index) =>
    createActivityReference(
      activity.id,
      activity.type,
      // Last share takes the remainder so 1/3 + 1/3 + 1/3 does not exceed 1.
      index === programs.length - 1 ? 1 - share * (programs.length - 1) : share,
    ),
  );
};
